import {ProductService} from "../../domain/product-service.entity";
import {
  ProductServiceSummary,
  ProductServiceOrderSummary,
  ProductServiceWorkDelivery,
  ProductServiceSalesInsights,
  ProductServiceCustomerPurchase,
  OrderListParams,
  ExportCustomerPurchasesParams,
  DeliverWorkInput,
  CreateProductServiceInput,
  UpdateProductServiceInput,
} from "../../domain/product-service.types";
import {httpClientV2} from "../../../../infrastructure/http";
import {ProductServiceMapperV2} from "../mappers/product-service.mapper.v2";
import {
  V2ProductServiceListItem,
  V2ProductServiceOrderListItem,
  V2WorkDeliveryItem,
  V2ProductServiceResponse,
  V2SalesInsightsResponse,
  V2CustomerPurchaseItem,
  PaginationParams,
  V2PaginatedResult,
} from "../mappers/product-service.mapper.types";
import {ProductServiceRepository} from "./product-service.repository";

/**
 * V2 create response body. Only the new id is needed — the full entity is
 * refetched via getSingle (the create response is partial: no currencies[] or
 * deliveryTimeframe fields).
 */
interface V2CreateProductServiceResponse {
  data: {id: number};
}

/**
 * V2 orders list response: { data: { data: [...], total, hasNextPage } }.
 */
interface V2GetOrdersResponse {
  data: {
    data: V2ProductServiceOrderListItem[];
    total: number;
    hasNextPage: boolean;
  };
}

/**
 * V2 order-detail response: { data: [work-delivery items] } (a bare array).
 */
interface V2GetOrderDeliveriesResponse {
  data: V2WorkDeliveryItem[];
}

/**
 * V2 sales-insights response: { data: {...} }.
 */
interface V2GetSalesInsightsResponse {
  data: V2SalesInsightsResponse;
}

/**
 * V2 customer-purchases response: { data: { data: [...], total, hasNextPage } }.
 */
interface V2GetCustomerPurchasesResponse {
  data: {
    data: V2CustomerPurchaseItem[];
    total: number;
    hasNextPage: boolean;
  };
}

/**
 * V2 single-service response body. httpClient returns the body, so the service
 * object lives under `data` (verified against the live API).
 */
interface V2GetProductServiceResponse {
  data: V2ProductServiceResponse;
}

/**
 * V2 list response body: { data: { data: [...], total, hasNextPage } }.
 */
interface V2GetListResponse {
  data: {
    data: V2ProductServiceListItem[];
    total: number;
    hasNextPage: boolean;
  };
}

/**
 * V2 API implementation of ProductServiceRepository.
 *
 * Pure TypeScript - no React imports.
 */
export class ProductServiceRepositoryV2 implements ProductServiceRepository {
  async getSingle(id: number): Promise<ProductService> {
    const response = await httpClientV2.get<V2GetProductServiceResponse>(
      `product-services/single/${id}`
    );

    return ProductServiceMapperV2.toDomain(response.data);
  }

  async getList(
    params: PaginationParams
  ): Promise<V2PaginatedResult<ProductServiceSummary>> {
    // V2 API uses camelCase: perPage
    // V2 returns nested structure: { data: { data: [...], total, hasNextPage } }
    const response = await httpClientV2.get<V2GetListResponse>(
      `product-services?page=${params.page}&perPage=${params.perPage}`
    );

    return {
      data: ProductServiceMapperV2.toSummaryList(response.data.data),
      total: response.data.total,
      hasNextPage: response.data.hasNextPage,
    };
  }

  async create(input: CreateProductServiceInput): Promise<ProductService> {
    const response = await httpClientV2.post<V2CreateProductServiceResponse>(
      "product-services",
      ProductServiceMapperV2.toCreateRequest(input)
    );

    // Create response is partial; refetch the full entity.
    return this.getSingle(response.data.id);
  }

  async update(input: UpdateProductServiceInput): Promise<ProductService> {
    await httpClientV2.put(
      "product-services",
      ProductServiceMapperV2.toUpdateRequest(input)
    );

    // Update response is partial (no currencies[]); refetch the full entity.
    return this.getSingle(input.serviceId);
  }

  async remove(id: number): Promise<void> {
    await httpClientV2.delete(`product-services/${id}`);
  }

  async getOrders(
    params: OrderListParams
  ): Promise<V2PaginatedResult<ProductServiceOrderSummary>> {
    const base = `product-services/orders?page=${params.page}&perPage=${params.perPage}`;
    const url = params.statusId === -1 ? base : `${base}&statusId=${params.statusId}`;

    const response = await httpClientV2.get<V2GetOrdersResponse>(url);

    return {
      data: ProductServiceMapperV2.toOrderSummaryList(response.data.data),
      total: response.data.total,
      hasNextPage: response.data.hasNextPage,
    };
  }

  async getOrderDeliveries(orderId: number): Promise<ProductServiceWorkDelivery[]> {
    // The v2 order-detail endpoint returns a bare array of work deliveries.
    const response = await httpClientV2.get<V2GetOrderDeliveriesResponse>(
      `product-services/order/${orderId}`
    );

    return ProductServiceMapperV2.toWorkDeliveryList(response.data);
  }

  async deliverWork(input: DeliverWorkInput): Promise<void> {
    // v2 multipart: `mediaUrls` (JSON string) + repeated `files` (verified in Postman).
    const formData = new FormData();
    formData.append("mediaUrls", JSON.stringify(input.mediaUrls));
    input.files.forEach((file) => formData.append("files", file));

    await httpClientV2.put(`product-services/delivery/${input.transactionId}`, formData);
  }

  async getSalesInsights(productServiceId: number): Promise<ProductServiceSalesInsights> {
    const response = await httpClientV2.get<V2GetSalesInsightsResponse>(
      `analytics/product-services/sales-insights/${productServiceId}`
    );

    return ProductServiceMapperV2.toSalesInsights(response.data);
  }

  async getCustomerPurchases(params: {
    productServiceId: number;
    page: number;
    perPage: number;
  }): Promise<V2PaginatedResult<ProductServiceCustomerPurchase>> {
    const response = await httpClientV2.get<V2GetCustomerPurchasesResponse>(
      `analytics/product-services/customer-purchases/${params.productServiceId}?page=${params.page}&perPage=${params.perPage}`
    );

    return {
      data: ProductServiceMapperV2.toCustomerPurchaseList(response.data.data),
      total: response.data.total,
      hasNextPage: response.data.hasNextPage,
    };
  }

  async exportCustomerPurchases(params: ExportCustomerPurchasesParams): Promise<Blob> {
    // responseType blob so both a CSV file and a JSON "empty" message come back
    // as a Blob; the caller branches on blob.type.
    return httpClientV2.get<Blob>(
      `analytics/product-services/customer-purchases/export/${params.productServiceId}?startDate=${params.startDate}&endDate=${params.endDate}`,
      {responseType: "blob"}
    );
  }
}
